import { useEffect, useRef } from 'react';
import { heroStats } from '@/mocks/homeData';

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationId: number;
    let particles: Array<{
      x: number;
      y: number;
      r: number;
      dx: number;
      dy: number;
      opacity: number;
    }> = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Create floating particles
    for (let i = 0; i < 30; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 3 + 1,
        dx: (Math.random() - 0.5) * 0.5,
        dy: (Math.random() - 0.5) * 0.5,
        opacity: Math.random() * 0.3 + 0.1,
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Transparent canvas — the teal gradient + image now live in CSS layers below,
      // so the animated particles float over the faded background image.

      // Draw particles
      particles.forEach((p) => {
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(245, 158, 11, ${p.opacity})`;
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-navy-800">
      {/* Base teal gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-600" />
      {/* Study-abroad artwork — kept light/subtle so the white text stays readable */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30 mix-blend-soft-light pointer-events-none"
        style={{ backgroundImage: "url('/assets/hero-bg.jpg')" }}
      />
      {/* Readability wash: darker at top/bottom, letting the artwork peek through the middle */}
      <div className="absolute inset-0 bg-gradient-to-b from-navy-900/70 via-navy-900/30 to-navy-900/80 pointer-events-none" />
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pt-24 pb-28">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight">
            Your Dream University Starts
            <br />
            <span className="text-gold-500">Here.</span>
          </h1>

          <p className="mt-6 md:mt-8 text-base md:text-lg lg:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
            Connect with top education consultancies near you. Book appointments, get AI-powered visa guidance, and take the first step toward studying abroad — all in one platform.
          </p>

          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => scrollToSection('#for-students')}
              className="px-8 py-3.5 bg-gold-500 text-navy-700 font-semibold rounded-md hover:bg-gold-400 transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              Find a Consultancy
              <i className="ri-arrow-right-line" />
            </button>
            <button
              onClick={() => scrollToSection('#for-consultancies')}
              className="px-8 py-3.5 border-2 border-white/60 text-white font-semibold rounded-md hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
            >
              I am a Consultancy
            </button>
          </div>

          {/* Stats */}
          <div className="mt-12 md:mt-16 flex flex-wrap items-center justify-center gap-8 md:gap-16">
            {heroStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-gold-500">{stat.number}</div>
                <div className="text-xs md:text-sm text-white/70 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator — anchored to the section bottom, clear of the stats row */}
      <button
        onClick={() => scrollToSection('#for-consultancies')}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 text-white/50 hover:text-white/80 text-xs animate-bounce cursor-pointer"
      >
        Scroll to explore
        <i className="ri-arrow-down-line text-base" />
      </button>
    </section>
  );
}